<template>
    <Calendar
        v-if="standaloneCalendar"
        v-model="selectedDate"
        v-bind="forwarded"
        initialFocus
        :placeholder="calendarPlaceholder"
    />

    <DsPopover
        v-else
        v-model:open="openPicker"
    >
        <DsPopoverTrigger>
            <slot name="trigger">
                <DsTextInput
                    v-bind="input"
                    class="min-w-max cursor-pointer"
                    :error="input?.error"
                    :forceFocusOutline="openPicker"
                    inputWrapperClasses="user-select-none pointer-events-none"
                    :modelValue="extractedDate"
                    readonly
                    :tabindex="-1"
                    type="date"
                    @click.stop="textInputClicked"
                >
                    <template #trailing>
                        <span class="flex items-end gap-2">
                            <button
                                aria-label="Clear date"
                                class="pointer-events-auto"
                                :class="selectedDate ? 'opacity-100' : 'opacity-0'"
                                tabindex="0"
                                @click.stop="selectedDate = undefined"
                                @keydown.enter="selectedDate = undefined"
                                @keydown.space.prevent="selectedDate = undefined"
                            >
                                <DsIcon
                                    icon="fa-solid fa-circle-xmark"
                                    size="md"
                                />
                            </button>
                            <button
                                aria-label="Open calendar"
                                class="pointer-events-auto"
                                tabindex="0"
                                @click.stop="openPicker = true"
                                @keydown.enter="openPicker = true"
                                @keydown.space.prevent="openPicker = true"
                            >
                                <DsIcon
                                    icon="fa-regular fa-calendar"
                                    size="md"
                                />
                            </button>
                        </span>
                    </template>
                </DsTextInput>
            </slot>
        </DsPopoverTrigger>
        <DsPopoverContent align="start">
            <Calendar
                v-model="selectedDate"
                v-bind="forwarded"
                initialFocus
                :placeholder="calendarPlaceholder"
            />
        </DsPopoverContent>
    </DsPopover>
</template>

<script setup lang="ts">
import { notifyError } from '@/bugsnag';
import DsIcon from '@/components/design-system/DsIcon.vue';
import Calendar from '@/components/design-system/form/date-picker/BaseCalendar.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import type { DsTextInputProps } from '@ds/form/input/text/text-input.types';
import { getLocalTimeZone, today } from '@internationalized/date';
import { reactiveOmit } from '@vueuse/core';
import type { CalendarRootEmits, CalendarRootProps, DateValue } from 'reka-ui';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, type HTMLAttributes, watch } from 'vue';

export type DsDatePickerProps = Omit<CalendarRootProps, 'modelValue'> & {
    /** Allows control of the underlying DsTextInput component */
    input?: Omit<DsTextInputProps, 'type' | 'readonly'>;
    standaloneCalendar?: boolean;
    class?: HTMLAttributes['class'];
    /** Controls whether the popover should close once a date is selected */
    closeOnSelect?: boolean;
};
export type DsDatePickerEmits = CalendarRootEmits & {
    click: [];
};

const props = withDefaults(defineProps<DsDatePickerProps>(), {
    standaloneCalendar: false,
    placeholder: () => today(getLocalTimeZone()),
    closeOnSelect: true,
});

const emit = defineEmits<DsDatePickerEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const selectedDate = defineModel<DateValue>();
const openPicker = defineModel<boolean>('openPicker', { default: false });

const calendarPlaceholder = computed<DateValue>(() => {
    if (selectedDate.value) {
        const singleDate = Array.isArray(selectedDate.value) ? selectedDate.value[0] : selectedDate.value;
        if (singleDate) {
            return singleDate;
        }
    }
    return props.placeholder ?? today(getLocalTimeZone());
});

const forwarded = useForwardPropsEmits(delegatedProps, emit);

const extractedDate = computed(() => {
    if (!selectedDate.value) {
        return '';
    }

    try {
        const dateValue = selectedDate.value;
        const singleDate = Array.isArray(dateValue) ? dateValue[0] : dateValue;
        if (!singleDate) {
            return '';
        }
        // serializes the date to a string in the format YYYY-MM-DD
        // and leave final formatting to the browser so locale is respected
        return singleDate.toString();
    } catch (error) {
        notifyError(error, 'DsDatePicker:extractedDate');
        return '';
    }
});

const textInputClicked = () => {
    emit('click');
    openPicker.value = true;
};

watch(selectedDate, () => {
    if (props.closeOnSelect) {
        openPicker.value = false;
    }
});
</script>
